import React, { useEffect, useState, useCallback } from "react";
import { t } from "i18next";
import i18n from "i18n";
import { Checkbox, CustomCheckbox, CustomInput } from "ui/Input";
import CustomButton, { TextButton } from "ui/Button";
import { COLOR, ICON_NAMES } from "utils/constant";
import styles from "styles.module.css";
import { Languages } from "lucide-react";
import { getCatchSignerUserApi } from "api/userAgreement";

import CustomDropDownList from "ui/dropDown";
import {
	getGetInternationalCountryCodeApi,
	getPostOfficeApi,
	getPostOfficesApi,
} from "api/common";
import { InternationalCountryCode, PostOffice } from "interface/common";
import { ListItemProps } from "@progress/kendo-react-dropdowns";
import { RegisterRecreationalForm } from "interface/recreationalFishers";
import { recreationalVesselRegistrationApi } from "api/vessel";
import { ErrorModal } from "ui/ErrorInfo";
import { useInfoStore } from "store/error";
import { CustomError } from "interface/index";

const defaultValue: RegisterRecreationalForm = {
	registrationNumber: "",
	usernameValid: false,
	ownerFirstName: "",
	ownerLastName: "",
	ownerAddress: "",
	ownerZip: "",
	ownerCity: "",
	ownerContactEmail: "",
	ownerBusinessRegistrationNumber: "",
	phonePrefix: "+47",
	ownerPhoneNumber: "",
	ownerBankAccount: "",
};
const nullToEmpty = (value: string | undefined | null) => {
	if (!value || value === "Null") {
		return "";
	}
	return value;
};

const sortCountryCodes = (codes: InternationalCountryCode[]) =>
	[...codes].sort((a, b) => {
		if (a.internationalDialing === "+47") return -1;
		if (b.internationalDialing === "+47") return 1;
		return a.country.localeCompare(b.country);
	});

const catchSignText =
	"For å registrere et fritidsfiskerfartøy må du ha en CatchSign-bruker. Har du ikke bruker fra før, må du registrere deg før du kan fortsette med registreringen av fartøyet.";

function RegisterRecreationalFishers() {
	const [form, setForm] = useState<RegisterRecreationalForm>(defaultValue);
	const [dataConfirmed, setDataConfirmed] = useState(false);
	const [hasNoRegistrationMark, setHasNoRegistrationMark] = useState(false);

	const [errors, setErrors] = useState<{ [key: string]: string }>({});
	const [currentLanguage, setCurrentLanguage] = useState<string>(i18n.language);
	const [showError, setShowError] = useState<boolean>(false);
	const [countryCodes, setCountryCodes] = React.useState<
		InternationalCountryCode[]
	>([]);
	const [countryCodesFilter, setCountryCodesFilter] = React.useState<
		InternationalCountryCode[]
	>([]);
	const [isMobileView, setIsMobileView] = useState(
		typeof window !== "undefined" ? window.innerWidth < 768 : false,
	);
	const [tempUsername, setTempUsername] = useState<string>("");
	const [isRegistering, setIsRegistering] = useState(false);

	const { setError: setInfoError, setInfo } = useInfoStore();

	const isValidEmail = (email: string) => {
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
	};

	const municipalCode = form.municipalCode?.trim() ?? "";

	const registrationNumber = hasNoRegistrationMark
		? /^\d{4}$/.test(municipalCode)
			? `ZZ${municipalCode}ZZ`
			: ""
		: form.registrationNumber;

	const validateForm = useCallback(() => {
		const newErrors: { [key: string]: string } = {};
		if (!dataConfirmed) newErrors.dataConfirmation = t("ConfirmationRequired");
		if (!form.ownerFirstName.trim())
			newErrors.vesselOwnerFirstname = t("Missing") + " " + t("FirstName");
		if (!form.ownerLastName.trim())
			newErrors.vesselOwnerLastname = t("Missing") + " " + t("LastName");
		if (!form.ownerBusinessRegistrationNumber.trim())
			newErrors.vesselOwnerIdentityNumber =
				t("Missing") + " " + t("NationalIdentityNumber");
		if (!form.ownerZip.trim())
			newErrors.vesselOwnerPostalCode = t("Missing") + " " + t("PostalCode");
		if (!form.ownerCity.trim())
			newErrors.vesselOwnerCity = t("Missing") + " " + t("City");
		if (!form.phonePrefix.trim())
			newErrors.phonePrefix = t("Missing") + " " + t("CountryCode");
		if (!form.ownerPhoneNumber.trim())
			newErrors.vesselOwnerPhoneNumber = t("Missing") + " " + t("PhoneNumber");
		if (form.ownerContactEmail.trim() && !isValidEmail(form.ownerContactEmail))
			newErrors.vesselOwnerEmail = t("InvalidEmail");
		if (!form.ownerBankAccount.trim())
			newErrors.vesselOwnerAccountNumber =
				t("Missing") + " " + t("AccountNumber");
		if (!registrationNumber.trim())
			newErrors.vesselRegNo = t("Missing") + " " + t("VesselRegistrationNo");

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	}, [form, dataConfirmed, registrationNumber]);

	useEffect(() => {
		getGetInternationalCountryCodeApi()
			.then((response) => {
				const sortedCountryCodes = sortCountryCodes(response);

				setCountryCodes(sortedCountryCodes);
				setCountryCodesFilter(sortedCountryCodes);
			})
			.catch((error) => {
				console.log(error);
			});
	}, []);

	useEffect(() => {
		if (showError) validateForm();
	}, [form, showError, validateForm]);

	useEffect(() => {
		const handleResize = () => {
			setIsMobileView(window.innerWidth < 768);
		};

		handleResize();
		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	const handleInputChange = (
		field: keyof RegisterRecreationalForm,
		value: string,
	) => {
		setForm((previousForm) => ({ ...previousForm, [field]: value }));
	};

	const handleSubmit = () => {
		if (isRegistering) return;

		const valid = validateForm();
		if (!valid) {
			setShowError(true);
			return;
		}

		proceedSubmit();
	};

	const proceedSubmit = async () => {
		if (!form) {
			setShowError(true);
			return;
		}

		setIsRegistering(true);
		try {
			await recreationalVesselRegistrationApi({
				...form,
				registrationNumber,

				ownerName: `${form.ownerFirstName} ${form.ownerLastName}`.trim(),
				ownerContactPhone: `${form.phonePrefix}${form.ownerPhoneNumber}`.trim(),
			});

			setInfo({
				show: true,
				title: t("Success"),
				infoType: "Success",
				description: t("VesselRegistered"),
			});

			setForm(defaultValue);
			setDataConfirmed(false);
			setHasNoRegistrationMark(false);
			setTempUsername("");
			setErrors({});
			setShowError(false);
		} catch (error: any) {
			setInfoError(new CustomError(""));
		} finally {
			setIsRegistering(false);
		}
	};

	const handleLanguageSwitch = (language: string) => {
		i18n.changeLanguage(language);
		setCurrentLanguage(language);
	};

	const handleFilterChange = (e: string) => {
		const searchText = e.trim();
		const dialingSearch = /^\d+$/.test(searchText)
			? `+${searchText}`
			: searchText;

		const filtered = countryCodes.filter(
			(code) =>
				code.internationalDialing.startsWith(dialingSearch) ||
				code.country.toLowerCase().startsWith(searchText.toLowerCase()),
		);
		const sorted = sortCountryCodes(filtered);
		setCountryCodesFilter(sorted);
	};

	const isValidUsername = (fieldValue?: string) => {
		if (!fieldValue) {
			return false;
		}

		const normalizedValue = fieldValue.trim().toUpperCase();
		const signaturePattern = /^\d{6}[A-Z]{6}$/;
		return signaturePattern.test(normalizedValue);
	};

	const validateUsername = () => {
		setErrors({});
		getCatchSignerUserApi(tempUsername)
			.then((response) => {
				if (response.isValid) {
					const _username = response.signUser.username.trim();
					if (
						_username.toLocaleLowerCase() !==
						tempUsername.trim().toLocaleLowerCase()
					) {
						return;
					}

					const signer = response.signUser;
					getPostOfficeApi(signer.zipCode)
						.then((data) => {
							setForm((prev) => ({
								...prev,
								usernameValid: response.isValid,
								ownerFirstName: signer.firstname,
								ownerLastName: signer.lastname,
								ownerAddress: nullToEmpty(signer.address),
								ownerZip: data.postCode,
								ownerCity: data.postName,
								ownerContactEmail: nullToEmpty(signer.email),
								ownerPhoneNumber: signer.phoneNumber,
								phonePrefix: "+47",
								municipalCode: data?.municipalCode ?? "",
							}));
						})
						.catch((err) => {
							setForm((prev) => ({
								...prev,
								usernameValid: response.isValid,
								ownerFirstName: signer.firstname,
								ownerLastName: signer.lastname,
								ownerAddress: nullToEmpty(signer.address),
								ownerContactEmail: nullToEmpty(signer.email),
								ownerPhoneNumber: signer.phoneNumber,
								phonePrefix: "+47",
							}));
						});
				} else {
					console.error("Invalid catch signer user");
					setForm((previousForm) => ({
						...previousForm,
						usernameValid: false,
					}));
					setErrors((prev) => ({
						...prev,
						usernameInValid:
							t("Invalid") + " " + t("CatchSign") + " " + t("Username"),
					}));
				}
			})
			.catch((error) => {
				console.error("Error fetching catch signer user:", error);
				setForm((previousForm) => ({
					...previousForm,
					usernameValid: false,
				}));
				setErrors((prev) => ({
					...prev,
					usernameInValid:
						t("Invalid") + " " + t("CatchSign") + " " + t("Username"),
				}));
			});
	};

	const invalidateUsername = () => {
		setForm((previousForm) => ({
			...previousForm,
			usernameValid: false,
		}));
	};

	return (
		<div
			className={`${styles.PageContainer} user-agreement-page`}
			style={{ minHeight: "89vh" }}
		>
			<div
				className="user-agreement-language-switch"
				style={{
					display: "flex",
					alignItems: "center",
					flexWrap: isMobileView ? "wrap" : "nowrap",
					gap: "10px",
					justifyContent: "center",
					marginBottom: "20px",
				}}
			>
				<div
					className="user-agreement-language-option"
					style={{
						display: "flex",
						alignItems: "center",
						gap: "10px",
						cursor: "pointer",
						padding: "8px 12px",
						borderRadius: "4px",
						backgroundColor:
							currentLanguage === "no" ? "#f0f0f0" : "transparent",
						border:
							currentLanguage === "no" ? "2px solid #007bff" : "1px solid #ccc",
					}}
					onClick={() => handleLanguageSwitch("no")}
				>
					<Languages color={currentLanguage === "no" ? "#007bff" : "#666"} />
					<span
						style={{
							color: currentLanguage === "no" ? "#007bff" : "#666",
							fontWeight: currentLanguage === "no" ? "bold" : "normal",
						}}
					>
						Norwegian
					</span>
				</div>
				<div
					className="user-agreement-language-option"
					style={{
						display: "flex",
						alignItems: "center",
						gap: "10px",
						cursor: "pointer",
						padding: "8px 12px",
						borderRadius: "4px",
						backgroundColor:
							currentLanguage === "en" ? "#f0f0f0" : "transparent",
						border:
							currentLanguage === "en" ? "2px solid #007bff" : "1px solid #ccc",
					}}
					onClick={() => handleLanguageSwitch("en")}
				>
					<Languages color={currentLanguage === "en" ? "#007bff" : "#666"} />
					<span
						style={{
							color: currentLanguage === "en" ? "#007bff" : "#666",
							fontWeight: currentLanguage === "en" ? "bold" : "normal",
						}}
					>
						English
					</span>
				</div>
			</div>
			<div className="page-header" style={{ textAlign: "center" }}>
				<legend className={styles.BoxHeadingLarge}>
					{t("RegisterRecreationalFishers")}
				</legend>
			</div>

			<div className="user-agreement-content">
				<div className="user-agreement-section">
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: "20px",
							marginBottom: "20px",
						}}
					>
						<div className={styles.BoxHeadingMedium} style={{ margin: 0 }}>
							{`${t("User")}`}
						</div>
					</div>

					<div className="user-agreement-two-column-row">
						<div className="user-agreement-column">
							<div
								className="user-agreement-user-fields"
								style={{
									display: "flex",
									flexDirection: "column",
									gap: "20px",
								}}
							>
								<div>
									<div style={{ display: "flex", gap: "10px" }}>
										<CustomInput
											label={t("CatchSign") + " " + t("Username")}
											placeholder={t("CatchSign") + " " + t("Username")}
											value={tempUsername}
											max={12}
											onChange={(value) => {
												if (
													value.trim().toUpperCase() !==
													tempUsername.trim().toUpperCase()
												) {
													setErrors((prev) => ({
														...prev,
														usernameInValid: "",
													}));
													invalidateUsername();
												}
												setTempUsername(value.trim().toUpperCase());
											}}
											inputStyle={{
												width: "100%",

												borderColor: errors.usernameInValid ? "red" : undefined,
											}}
										/>
										<div style={{ marginTop: "24px" }}>
											{form.usernameValid ? (
												<img
													src={`icon/${ICON_NAMES.CheckGreen}.svg`}
													alt={"username valid"}
													width={30}
													height={30}
												/>
											) : (
												<CustomButton
													label={t("Validate")}
													bgColor={COLOR.Green}
													color="white"
													onClick={validateUsername}
													disabled={!isValidUsername(tempUsername.trim())}
													btnStyle={
														isMobileView
															? { width: "100%" }
															: { height: "32px" }
													}
												/>
											)}
										</div>
									</div>
									<div>
										<p style={{ fontSize: "13px", marginBottom: "2px" }}>
											{catchSignText}
										</p>
										<div>
											<span style={{ fontSize: "13px" }}>
												<a
													href="https://www.surofi.no/catchsign"
													target="_blank"
													rel="noopener noreferrer"
												>
													Se hvordan du gjør det her
												</a>
											</span>
										</div>
									</div>

									{errors.usernameInValid && (
										<div
											style={{
												color: "red",
												fontSize: "12px",
												marginTop: "4px",
											}}
										>
											{errors.usernameInValid}
										</div>
									)}
								</div>
							</div>
						</div>
						<div className="user-agreement-column user-agreement-column-spacer"></div>
					</div>
				</div>

				{form.usernameValid && (
					<div>
						<div className="user-agreement-section">
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: "20px",
									marginBottom: "20px",
								}}
							>
								<div className={styles.BoxHeadingMedium} style={{ margin: 0 }}>
									{`${t("VesselOwner")}`}
								</div>
							</div>

							<div className="user-agreement-two-column-row">
								<div className="user-agreement-column">
									<div
										className="user-agreement-user-fields"
										style={{
											display: "flex",
											flexDirection: "column",
											gap: "20px",
										}}
									>
										<div>
											<CustomInput
												label={t("FirstName")}
												placeholder={t("FirstName")}
												value={form.ownerFirstName}
												readOnly={!form.usernameValid}
												onChange={(value) =>
													handleInputChange("ownerFirstName", value)
												}
												inputStyle={{
													width: "100%",
													borderColor: errors.vesselOwnerFirstname
														? "red"
														: undefined,
												}}
											/>
											{errors.vesselOwnerFirstname && (
												<div
													style={{
														color: "red",
														fontSize: "12px",
														marginTop: "4px",
													}}
												>
													{errors.vesselOwnerFirstname}
												</div>
											)}
										</div>

										<div>
											<CustomInput
												label={t("LastName")}
												placeholder={t("LastName")}
												value={form.ownerLastName}
												readOnly={!form.usernameValid}
												onChange={(value) =>
													handleInputChange("ownerLastName", value)
												}
												inputStyle={{
													width: "100%",
													borderColor: errors.vesselOwnerLastname
														? "red"
														: undefined,
												}}
											/>
											{errors.vesselOwnerLastname && (
												<div
													style={{
														color: "red",
														fontSize: "12px",
														marginTop: "4px",
													}}
												>
													{errors.vesselOwnerLastname}
												</div>
											)}
										</div>
										<div>
											<CustomInput
												label={t("Address")}
												placeholder={t("Address")}
												value={form.ownerAddress}
												readOnly={!form.usernameValid}
												onChange={(value) =>
													handleInputChange("ownerAddress", value)
												}
												isError={!!errors.vesselOwnerAddress}
											/>
											{errors.vesselOwnerAddress && (
												<div
													style={{
														color: "red",
														fontSize: "12px",
														marginTop: "4px",
													}}
												>
													{errors.vesselOwnerAddress}
												</div>
											)}
										</div>
										<div>
											<div
												className="user-agreement-phone-row"
												style={{
													display: "flex",
													flexDirection: isMobileView ? "column" : "row",
													gap: "10px",
												}}
											>
												<div style={{ width: isMobileView ? "100%" : "50%" }}>
													<span style={{ paddingBottom: "4px", display: "block" }}>
														{t("PostalCode")}
													</span>
													<CustomDropDownList
														data={[]}
														dataItemKey="postCode"
														textField="postCode"
														filterable={true}
														serverFilter={true}
														// label={t("CountryCode")}
														// placeholder={t("CountryCode")}
														serverFilterApi={getPostOfficesApi}
														value={{
															postCode: form.ownerZip,
														}}
														onChange={(e) => {
															setForm((prev) => ({
																...prev,
																ownerZip: e.value?.postName ? e.value.postCode : "",
																ownerCity: e.value?.postName ?? "",
																municipalCode: e.value?.municipalCode ?? "",
															}));
														}}
														onOpen={() => {}}
														// disabled={!form.usernameValid}
														defaultItem={{
															postCode: t("Choose"),
														}}
														itemRender={(
															li: any, // React.ReactElement<HTMLLIElement>,
															itemProps: ListItemProps,
														) => {
															const data: PostOffice = itemProps.dataItem;

															const item = (
																<div
																	style={{
																		display: "flex",
																		alignItems: "center",
																		gap: "12px",
																	}}
																>
																	<span style={{ width: "50px" }}>
																		{data.postCode}
																	</span>
																	<span>{data.postName}</span>
																</div>
															);

															return React.cloneElement(li, li.props, item);
														}}
														showError={!!errors.vesselOwnerPostalCode}
													/>
													{errors.vesselOwnerPostalCode && (
														<div
															style={{
																color: "red",
																fontSize: "12px",
																marginTop: "4px",
															}}
														>
															{errors.vesselOwnerPostalCode}
														</div>
													)}
												</div>
												<div style={{ width: isMobileView ? "100%" : "50%" }}>
													<CustomInput
														label={t("City")}
														placeholder={t("City")}
														value={form.ownerCity}
														readOnly={true}
														onChange={(value) =>
															handleInputChange("ownerCity", value)
														}
														isError={!!errors.vesselOwnerCity}
													/>
													{errors.vesselOwnerCity && (
														<div
															style={{
																color: "red",
																fontSize: "12px",
																marginTop: "4px",
															}}
														>
															{errors.vesselOwnerCity}
														</div>
													)}
												</div>
											</div>
										</div>

										<div>
											<div
												className="user-agreement-phone-row"
												style={{
													display: "flex",
													flexDirection: isMobileView ? "column" : "row",
													gap: "10px",
												}}
											>
												<div style={{ width: isMobileView ? "100%" : "50%" }}>
													<span
														style={{ paddingBottom: "4px", display: "block" }}
													>
														{t("CountryCode")}
													</span>
													<CustomDropDownList
														data={countryCodesFilter}
														//data={[]}
														dataItemKey="internationalDialing"
														textField="internationalDialing"
														filterable={true}
														// label={t("CountryCode")}
														// placeholder={t("CountryCode")}
														handleFilterChange={(e) => {
															if (e.filter) {
																handleFilterChange(e.filter.value);
															}
														}}
														value={{
															internationalDialing: form.phonePrefix,
														}}
														onChange={(e) => {
															const selectedPrefix = e.value.internationalDialing;
															handleInputChange("phonePrefix", selectedPrefix);
															// console.log("changed", e);
														}}
														onOpen={() => {}}
														disabled={!form.usernameValid}
														defaultItem={{
															internationalDialing: t("Choose"),
														}}
														itemRender={(
															li: any, // React.ReactElement<HTMLLIElement>,
															itemProps: ListItemProps,
														) => {
															const data: InternationalCountryCode =
																itemProps.dataItem;

															const item = (
																<div
																	style={{
																		display: "flex",
																		alignItems: "center",
																		gap: "12px",
																	}}
																>
																	<span style={{ width: "50px" }}>
																		{data.internationalDialing}
																	</span>
																	<span>{data.country}</span>
																</div>
															);

															return React.cloneElement(li, li.props, item);
														}}
														showError={!!errors.phonePrefix}
													/>
												</div>
												<div style={{ width: isMobileView ? "100%" : "50%" }}>
													<CustomInput
														label={t("PhoneNumber")}
														placeholder={t("PhoneNumber")}
														value={form.ownerPhoneNumber}
														type="number"
														readOnly={!form.usernameValid}
														onChange={(value) => {
															handleInputChange("ownerPhoneNumber", value);
														}}
														inputStyle={{
															width: "100%",
															borderColor: errors.vesselOwnerPhoneNumber
																? "red"
																: undefined,
														}}
													/>
												</div>
											</div>
											{errors.vesselOwnerPhoneNumber && (
												<div
													style={{
														color: "red",
														fontSize: "12px",
														marginTop: "4px",
													}}
												>
													{errors.vesselOwnerPhoneNumber}
												</div>
											)}
											{errors.phonePrefix && (
												<div
													style={{
														color: "red",
														fontSize: "12px",
														marginTop: "4px",
													}}
												>
													{errors.phonePrefix}
												</div>
											)}
										</div>
										<div>
											<CustomInput
												label={t("Email")}
												placeholder={t("Email")}
												value={form.ownerContactEmail}
												readOnly={!form.usernameValid}
												onChange={(value) =>
													handleInputChange("ownerContactEmail", value)
												}
												inputStyle={{
													width: "100%",
													borderColor: errors.vesselOwnerEmail
														? "red"
														: undefined,
												}}
											/>
											{errors.vesselOwnerEmail && (
												<div
													style={{
														color: "red",
														fontSize: "12px",
														marginTop: "4px",
													}}
												>
													{errors.vesselOwnerEmail}
												</div>
											)}
										</div>




										<div>
											<div
												className="user-agreement-phone-row"
												style={{
													display: "flex",
													flexDirection: isMobileView ? "column" : "row",
													gap: "10px",
												}}
											>
												<div style={{ width: isMobileView ? "100%" : "50%" }}>
													<CustomInput
														label={t("NationalIdentityNumber")}
														placeholder={t("NationalIdentityNumber")}
														value={form.ownerBusinessRegistrationNumber}
														readOnly={!form.usernameValid}
														onChange={(value) =>
															handleInputChange(
																"ownerBusinessRegistrationNumber",
																value,
															)
														}
														isError={!!errors.vesselOwnerIdentityNumber}
													/>
													{errors.vesselOwnerIdentityNumber && (
														<div
															style={{
																color: "red",
																fontSize: "12px",
																marginTop: "4px",
															}}
														>
															{errors.vesselOwnerIdentityNumber}
														</div>
													)}
												</div>

												<div style={{ width: isMobileView ? "100%" : "50%" }}>
													<CustomInput
														label={t("AccountNumber")}
														placeholder={t("AccountNumber")}
														value={form.ownerBankAccount}
														readOnly={!form.usernameValid}
														onChange={(value) =>
															handleInputChange("ownerBankAccount", value)
														}
														isError={!!errors.vesselOwnerAccountNumber}
														//inputStyle={{
														//	width: "100%",
														//	borderColor: errors.vesselOwnerAccountNumber
														//		? "red"
														//		: undefined,
														//}}
													/>
													{errors.vesselOwnerAccountNumber && (
														<div
															style={{
																color: "red",
																fontSize: "12px",
																marginTop: "4px",
															}}
														>
															{errors.vesselOwnerAccountNumber}
														</div>
													)}
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="user-agreement-column user-agreement-column-spacer"></div>
							</div>


						</div>
						<div className="user-agreement-section">
							<h2
								style={{ marginBottom: "20px" }}
								className={styles.BoxHeadingMedium}
							>
								{t("Vessel")}
							</h2>

							<div
								className="user-agreement-two-column-row"
								style={{ marginBottom: "20px" }}
							>
								<div
									className="user-agreement-column"
									style={{
										flex: 1,
										display: "flex",
										flexDirection: "column",
										gap: "10px",
									}}
								>
									<div>
										<CustomCheckbox
											label={t("HasNoRegistrationMark")}
											value={hasNoRegistrationMark}
											onChange={setHasNoRegistrationMark}
										/>
									</div>

									<CustomInput
										label={t("VesselRegistrationNumber")}
										value={registrationNumber}
										disabled={hasNoRegistrationMark}
										onChange={(v) => handleInputChange("registrationNumber", v)}
										isError={!!errors.vesselRegNo}
										placeholder={t("VesselRegistrationNumber")}
									/>
								</div>
								<div className="user-agreement-column user-agreement-column-spacer"></div>
							</div>
						</div>

					</div>
				)}


				<ErrorModal />
				<div className="user-agreement-section">
					<Checkbox
						label={t("RecreationalVesselRegistrationConfirmation")}
						value={dataConfirmed}
						onChange={setDataConfirmed}
						showError={!!errors.dataConfirmation}
						labelStyle={{ alignItems: "flex-start", lineHeight: 1.5 }}
						inputStyle={{ flexShrink: 0, marginTop: "4px" }}
					/>
					{errors.dataConfirmation && (
						<div
							role="alert"
							style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
						>
							{errors.dataConfirmation}
						</div>
					)}
				</div>
				<div
					className="user-agreement-actions-row"
					style={{
						marginTop: "40px",
						display: "flex",
						justifyContent: "center",
					}}
				>
					<CustomButton
						label={isRegistering ? t("Loading") : t("ConfirmRegistration")}
						bgColor={COLOR.Green}
						color="white"
						disabled={!form.usernameValid || !dataConfirmed || isRegistering}
						onClick={handleSubmit}
						btnStyle={isMobileView ? { width: "100%" } : undefined}
					/>
				</div>
			</div>
		</div>
	);
}

export default RegisterRecreationalFishers;
